import React, { useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'

import { baseRequest } from '../../_common/core/http_services'
import modalStyle from '../../_common/core/modalStyle'
import Button from '../_library/Button'
import { showAxiosError } from '../utils/messenger'

const MIN_LOADING_MS = 2000
const SUCCESS_VISIBLE_MS = 7000
const DASHBOARD_FEEDBACK_SOURCE = 'Dashboard 2.0'

const toastStyles = {
  wrapper: {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 24,
    zIndex: 1000002,
    maxWidth: 'calc(100% - 48px)',
    minWidth: 320,
    pointerEvents: 'none',
  },
}

const GiveFeedback = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSuccessExiting, setIsSuccessExiting] = useState(false)

  const textareaRef = useRef(null)
  const timersRef = useRef([])
  const clearTimers = () => {
    for (const t of timersRef.current) window.clearTimeout(t)
    timersRef.current = []
  }

  useEffect(() => () => clearTimers(), [])

  useEffect(() => {
    if (!isOpen) return
    try {
      textareaRef.current?.focus?.()
    } catch (_) {
      // ignore
    }

    const onKeyDown = e => {
      if (e?.key === 'Escape') setIsOpen(false)
    }

    try {
      window?.addEventListener?.('keydown', onKeyDown)
    } catch (_) {
      // ignore
    }

    return () => {
      try {
        window?.removeEventListener?.('keydown', onKeyDown)
      } catch (_) {
        // ignore
      }
    }
  }, [isOpen])

  const showSuccessToast = () => {
    clearTimers()
    setShowSuccess(true)
    setIsSuccessExiting(false)

    timersRef.current.push(
      window.setTimeout(() => {
        setIsSuccessExiting(true)
        timersRef.current.push(
          window.setTimeout(() => {
            setShowSuccess(false)
            setIsSuccessExiting(false)
          }, 230),
        )
      }, SUCCESS_VISIBLE_MS),
    )
  }

  const submit = async () => {
    const reason = String(message || '').trim()
    if (!reason || loading) return

    let page = null
    try {
      page = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`.slice(0, 255)
    } catch (_) {
      page = null
    }

    try {
      setLoading(true)
      const apiPromise = baseRequest.post('/feedback', {
        message: reason,
        page,
        source: DASHBOARD_FEEDBACK_SOURCE,
      })
      const minTimePromise = new Promise(resolve => window.setTimeout(resolve, MIN_LOADING_MS))
      await Promise.all([apiPromise, minTimePromise])

      setIsOpen(false)
      setMessage('')
      showSuccessToast()
    } catch (err) {
      showAxiosError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {showSuccess && (
        <div style={toastStyles.wrapper} role="region" aria-label="Feedback submitted">
          <div className={`alert alert-success ${isSuccessExiting ? 'fade' : ''}`} style={{ pointerEvents: 'auto' }}>
            <button
              type="button"
              className="close"
              aria-label="Dismiss"
              onClick={() => {
                clearTimers()
                setShowSuccess(false)
                setIsSuccessExiting(false)
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
            <strong>Thank you for your feedback!</strong> We use messages like yours to improve our platform.
          </div>
        </div>
      )}

      <div
        className="feedback-link"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={e => {
          if (e?.key === 'Enter' || e?.key === ' ') setIsOpen(true)
        }}
      >
        <i className="fa fa-comment-o" />
        Give feedback
      </div>

      <Modal
        className="modal-dialog modal-trans ttf-feedback-modal"
        style={modalStyle}
        isOpen={isOpen}
        contentLabel="Give feedback"
        onRequestClose={() => setIsOpen(false)}
        closeTimeoutMS={150}
        ariaHideApp={false}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div>
              <div className="modal-header">
                <button
                  type="button"
                  className="close ttf-feedback-modal__close"
                  onClick={() => setIsOpen(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
                <p className="h4 text-compact">Share your feedback</p>
              </div>
              <div className="modal-body">
                <p className="text-muted">Tell us what’s working well or what could be improved.</p>
                <div className="div-spacing-10" />
                <textarea
                  ref={textareaRef}
                  className="form-control"
                  rows={5}
                  placeholder="Write your feedback…"
                  value={message}
                  onChange={e => setMessage(e?.target?.value || '')}
                  disabled={loading}
                />
              </div>
              <div className="modal-footer">
                <div className="btn-toolbar btn-toolbar-right">
                  <Button
                    className="btn btn-primary"
                    type="button"
                    onClick={submit}
                    loading={loading}
                    disabled={loading || !String(message || '').trim()}
                  >
                    Share feedback
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default GiveFeedback

